import { makeStyles } from "tss-react/mui"
import { useState } from "react"
import { faBell } from "@fortawesome/pro-light-svg-icons"
import { Box, IconButton, Menu, Skeleton, Stack, Tooltip, Typography, useTheme } from "@mui/material"
import { useApiQuery } from "../../../../core/api/useApiQuery"
import {
    type Notification,
    fetchNotificationCount,
    FetchNotificationCountResponse,
    fetchNotifications,
    FetchNotificationsResponse,
    NotificationQuery,
} from "./notificationApi"
import { FontAwesomeSvgIcon } from "../NewsBellNavItem/NewsBellNavItem"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { DateTimeFactory, useDateTime } from "../../../../modules/localization/useDateTime"
import { LocalizedEnum, LocalizedStrict } from "../../../../modules/localization/components/AppLocalized"
import {
    MessageTagIcon,
    notificationOptions,
} from "../../../../modules/merchant/admin/messages/components/MessageTagAutoComplete"
import { faBell as faBellSolid, faCircle } from "@fortawesome/pro-solid-svg-icons"
import { EmptyContent } from "../../../components/skeleton/EmptyContent"
import { QueryErrorBoundary } from "../../../../core/api/components/QueryErrorBoundary"

const useStyles = makeStyles()((theme) => ({
    iconButton: {
        color: theme.palette.tailwind.neutral[500],
    },
    menu: {
        "& .MuiPaper-root": {
            width: "30rem",
            padding: "1rem 1.5rem",
            borderRadius: ".5rem",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
        },
    },
    menuTitle: {},
    menuItem: {
        padding: 0,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: ".5rem",
        "&:hover": {
            backgroundColor: "white",
        },
    },
    dateContainer: {
        display: "flex",
        alignItems: "center",
        gap: ".5rem",
        marginTop: ".25rem",
    },
    clockIcon: {
        color: theme.palette.grey[500],
        fontSize: "1rem",
    },
    counterBox: {
        background: "#2196F3",
        borderRadius: "50%",
        color: theme.palette.common.white,
        padding: "0rem 0.406rem",
        fontSize: "0.7rem",
        lineHeight: "1rem",
        letterSpacing: "0.009rem",
        textAlign: "center",
        fontWeight: 500,
        minWidth: "1.5rem",
        height: "1.5rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    counter: {
        position: "absolute",
        top: "0rem",
        left: "1.75rem",
    },
    emptyContentContainer: {
        height: "60vh",
    },
    contentContainer: {
        paddingTop: "2rem",
    },
    innerCard: {
        padding: "0.5rem",
        display: "flex",
        flexDirection: "column",
        gap: "0.625rem",
        width: "100%",
    },
    subject: {
        fontWeight: 500,
    },
    cardHeader: {
        display: "flex",
        alignItems: "center",
        gap: "0.625rem",
    },
    messageTime: {
        alignItems: "center",
        display: "flex",
        gap: "0.75rem",
    },
    colorBox: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "none",
        width: "1.5rem",
        height: "1.5rem",
        borderRadius: "100%",
        color: theme.palette.common.white,
        "& > svg": {
            fontSize: ".75rem",
        },
    },
}))

function selectCount(response: FetchNotificationCountResponse) {
    return response.countNotifications
}

function selectNotifications(response: FetchNotificationsResponse) {
    return response.notifications
}

type NotificationItemProps = Readonly<{
    notification: Notification | undefined
    dateTimeFactory: DateTimeFactory
}>

function NotificationItem({ notification, dateTimeFactory }: NotificationItemProps) {
    const { classes } = useStyles()

    if (!notification) {
        return <Skeleton variant="rounded" animation="wave" width={410} height={100} />
    }

    const time = dateTimeFactory.fromApi(notification.created)
    const selectedOption = notificationOptions.find((opt) => opt.value === notification.tag) ?? null

    return (
        <Box className={classes.menuItem}>
            <Box className={classes.innerCard}>
                <Box className={classes.cardHeader}>
                    {selectedOption && (
                        <MessageTagIcon
                            color={selectedOption.color}
                            icon={selectedOption.icon}
                            className={classes.colorBox}
                        />
                    )}
                    <Typography variant="body1" className={classes.subject}>
                        {notification.subject}
                    </Typography>
                </Box>
                <Typography variant="body1" whiteSpace="normal">
                    {notification.message}
                </Typography>
                <Box className={classes.messageTime}>
                    {notification.tag && (
                        <>
                            <LocalizedEnum
                                base="user-home-top-bar-news-notification-time-type"
                                enumValue={notification.tag}
                            >
                                <Typography color="textSecondary" variant="caption">
                                    Nyhed
                                </Typography>
                            </LocalizedEnum>
                            <FontAwesomeIcon icon={faCircle} color="#94A3B8" width="5px" />
                        </>
                    )}
                    <Typography variant="caption" color="textSecondary">
                        {time.toRelative({ style: "narrow" })}
                    </Typography>
                </Box>
            </Box>
        </Box>
    )
}

type NotificationListProps = Readonly<{}>

function NotificationList({}: NotificationListProps) {
    const { classes } = useStyles()
    const theme = useTheme()
    const dateTimeFactory = useDateTime()

    const { data: notifications, isLoading } = useApiQuery(fetchNotifications, {
        queryName: NotificationQuery.NOTIFICATIONS_LIST,
        dependencies: {},
        select: selectNotifications,
        useErrorBoundary: true,
        staleTime: 0,
    })

    if (isLoading) {
        return (
            <Stack spacing="1rem" className={classes.contentContainer}>
                <NotificationItem notification={undefined} dateTimeFactory={dateTimeFactory} />
                <NotificationItem notification={undefined} dateTimeFactory={dateTimeFactory} />
            </Stack>
        )
    }

    if (notifications) {
        if (notifications.length === 0) {
            return (
                <div className={classes.emptyContentContainer}>
                    <LocalizedStrict
                        id="user-home-top-bar-news-no-notifications"
                        attrs={{ title: true, description: true }}
                    >
                        <EmptyContent
                            icon={faBellSolid}
                            backgroundColor={theme.palette.tailwind.pink[500]}
                            title="No messages right now"
                            description="Here you will be able to read messages from the merchant and service center."
                        />
                    </LocalizedStrict>
                </div>
            )
        } else {
            return (
                <Stack spacing="1rem" className={classes.contentContainer}>
                    {notifications.map((notification) => (
                        <NotificationItem
                            key={notification.id}
                            notification={notification}
                            dateTimeFactory={dateTimeFactory}
                        />
                    ))}
                </Stack>
            )
        }
    }

    throw new Error("Internal error in component")
}

type NotificationTopBarProps = Readonly<{}>

export function NotificationTopBarButton({}: NotificationTopBarProps) {
    const { classes, cx } = useStyles()

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)

    const { data: notificationCount } = useApiQuery(fetchNotificationCount, {
        queryName: NotificationQuery.NOTIFICATION_COUNT,
        dependencies: {},
        select: selectCount,
        refetchInterval: 60000,
    })

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    return (
        <>
            <LocalizedStrict id="notifications-tooltip-text" attrs={{ title: true }}>
                <Tooltip title="View notifications">
                    <IconButton onClick={handleClick} size="medium" className={cx(classes.iconButton)}>
                        <FontAwesomeSvgIcon icon={faBell} />
                        {!!notificationCount && (
                            <Box className={cx(classes.counter, classes.counterBox)}>
                                {notificationCount > 99 ? "99" : notificationCount}
                            </Box>
                        )}
                    </IconButton>
                </Tooltip>
            </LocalizedStrict>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                className={classes.menu}
            >
                <Stack spacing="1rem">
                    <LocalizedStrict id="user-home-top-bar-news-notification-menu-title">
                        <Typography variant="body1" fontWeight={500} align="center" className={classes.menuTitle}>
                            Notifications
                        </Typography>
                    </LocalizedStrict>
                    <QueryErrorBoundary>
                        <NotificationList />
                    </QueryErrorBoundary>
                </Stack>
            </Menu>
        </>
    )
}
